import { useMemo, useState } from 'react';
import Reveal from '@/components/base/Reveal';
import SectionHeading from '@/components/base/SectionHeading';
import { useToast } from '@/components/base/ToastProvider';
import { rankingCategories, rankingItems, topicRising } from '@/mocks/home';
import { formatHeat, formatRank } from '@/pages/home/utils';

export default function RankingSection() {
  const [active, setActive] = useState('all');
  const { showToast } = useToast();

  const list = useMemo(() => {
    if (active === 'all') {
      return [...rankingItems].sort((a, b) => b.heat - a.heat).slice(0, 10);
    }
    return rankingItems
      .filter((item) => item.category === active)
      .sort((a, b) => b.heat - a.heat);
  }, [active]);

  return (
    <section id="rank" className="scroll-mt-header w-full bg-background-100 py-14 md:py-20">
      <div className="mx-auto w-full max-w-[1240px] px-4 md:px-8">
        <Reveal>
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <SectionHeading
              index="01"
              eyebrow="Daily Ranking"
              title="今日黑料 TOP10"
              desc="按公开讨论量、扩散速度与新增信息条数综合计算热度值，热度越高排名越靠前。切换分类可查看各圈层的完整榜单。"
            />
            <div className="flex flex-wrap gap-1 rounded-full border border-background-200 bg-background-50 p-1">
              {rankingCategories.map((tab) => {
                const isActive = tab.key === active;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setActive(tab.key)}
                    className={`rounded-full px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
                      isActive
                        ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                        : 'text-foreground-700 hover:bg-background-100 hover:text-primary-700'
                    }`}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>
        </Reveal>

        <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <div className="flex flex-col gap-3">
              {list.map((item, index) => {
                const isTop3 = index < 3;
                return (
                  <Reveal key={item.id} delay={index * 40}>
                    <article className="group flex items-center gap-4 rounded-lg border border-background-200/70 bg-background-50 p-3 transition-colors hover:border-primary-300 md:gap-5 md:p-4">
                      <div className="flex w-9 shrink-0 justify-center md:w-11">
                        <span
                          className={`font-heading text-xl font-bold md:text-[28px] ${
                            isTop3 ? 'text-primary-600' : 'text-foreground-300'
                          }`}
                        >
                          {formatRank(index)}
                        </span>
                      </div>

                      <div className="h-16 w-16 shrink-0 overflow-hidden rounded-md border border-background-200/70 md:h-20 md:w-20">
                        <img
                          src={item.cover}
                          alt={`${item.title} 热点黑料榜单条目配图`}
                          title={`${item.title} 热点黑料盘点`}
                          className="h-full w-full object-cover object-top"
                        />
                      </div>

                      <div className="min-w-0 flex-1">
                        <h3 className="truncate text-sm font-semibold text-foreground-950 transition-colors group-hover:text-primary-700 md:text-base">
                          {item.title}
                        </h3>
                        <p className="mt-1.5 hidden truncate text-xs text-foreground-600 sm:block">
                          {item.summary}
                        </p>
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          {item.tags.map((tag) => (
                            <span
                              key={tag}
                              className="rounded-full bg-secondary-100 px-2.5 py-1 text-[11px] font-medium text-secondary-900"
                            >
                              {tag}
                            </span>
                          ))}
                          <span className="text-[11px] text-foreground-400">{item.updatedAt} 更新</span>
                        </div>
                      </div>

                      <div className="hidden shrink-0 flex-col items-end gap-2 md:flex">
                        <span className="flex items-center gap-1 font-heading text-base font-bold text-primary-600">
                          <i className="ri-fire-line flex h-3.5 w-3.5 items-center justify-center text-sm" />
                          {formatHeat(item.heat)}
                        </span>
                        <button
                          type="button"
                          onClick={() => showToast('条目详情正在整理中，稍后可在合集里查看完整时间线')}
                          className="rounded-md border border-background-200 px-3 py-1.5 text-xs font-medium whitespace-nowrap text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
                        >
                          查看详情
                        </button>
                      </div>
                    </article>
                  </Reveal>
                );
              })}
            </div>
          </div>

          <aside className="lg:col-span-4">
            <Reveal delay={100}>
              <div className="rounded-lg border border-background-200/70 bg-background-50 p-5 md:p-6">
                <div className="flex items-center gap-2">
                  <span className="flex h-7 w-7 items-center justify-center rounded-md bg-accent-500 text-background-50 dark:text-foreground-950">
                    <i className="ri-line-chart-line flex h-4 w-4 items-center justify-center text-base" />
                  </span>
                  <h3 className="font-heading text-base font-bold text-foreground-950">热度上升最快</h3>
                </div>
                <ul className="mt-5 flex flex-col divide-y divide-background-200/70">
                  {topicRising.map((topic, index) => (
                    <li key={topic.name} className="flex items-center gap-3 py-3.5">
                      <span className="font-heading text-sm font-bold text-foreground-300">
                        {formatRank(index)}
                      </span>
                      <span className="min-w-0 flex-1 truncate text-sm text-foreground-800">
                        {topic.name}
                      </span>
                      <span className="shrink-0 rounded-full bg-primary-100 px-2.5 py-1 text-[11px] font-semibold text-primary-800">
                        {topic.delta}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>

            <Reveal delay={160}>
              <div className="mt-5 rounded-lg border border-background-200/70 bg-secondary-50 p-5 md:p-6">
                <h3 className="font-heading text-base font-bold text-foreground-950">榜单怎么看</h3>
                <ul className="mt-4 flex flex-col gap-3 text-[13px] leading-6 text-foreground-700">
                  <li className="flex gap-2.5">
                    <i className="ri-checkbox-circle-line flex h-4 w-4 shrink-0 items-center justify-center text-base text-primary-600" />
                    热度值为综合指标，不等于事件严重程度
                  </li>
                  <li className="flex gap-2.5">
                    <i className="ri-checkbox-circle-line flex h-4 w-4 shrink-0 items-center justify-center text-base text-primary-600" />
                    同一条目多次上榜时，会合并为一条记录
                  </li>
                  <li className="flex gap-2.5">
                    <i className="ri-checkbox-circle-line flex h-4 w-4 shrink-0 items-center justify-center text-base text-primary-600" />
                    时间线以当事方公开发声顺序为准
                  </li>
                </ul>
              </div>
            </Reveal>
          </aside>
        </div>
      </div>
    </section>
  );
}